import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceInterface } from '../../../interfaces/invoice.interface';
import { getVivaAuthenticationCode, getVivaLicenseKey, getVivaUniqueDocId } from '../../../services/viva/viva.utils';

interface Props {
  doc: InvoiceInterface;
}

// The QR itself lives in `DocViewQrCode.tsx` now, rendered next to `DocViewNotes` instead of here
// — this component still covers the rest of the AADE/provider data (UID, MARK, provider signature
// info) and the Publisher/Receiver signature-line labels, both still shown in the page-edge footer.
export const DocViewAadeData = ({ doc }: Props) => {
  const { t } = useTranslation();
  const signatures = useMemo(() => doc?.providerData?.fiscalisationDetails?.ftSignatures || [], [doc]);

  const getProviderAuthCode = useCallback(() => {
    const providerAuthCode = getVivaAuthenticationCode(signatures);
    if (providerAuthCode) {
      return (
        <div className="flex-start-center-row-wrap height-14">
          <div className="normalText0 flex-start-center-row-nowrap">
            {providerAuthCode} (<div className="boldText0">{t('PDF.PROVIDER_AUTH_CODE')}</div>)
          </div>
        </div>
      );
    }
  }, [signatures, t]);

  const getProviderLicenseKey = useCallback(() => {
    const providerLicenseKey = getVivaLicenseKey(signatures);
    if (providerLicenseKey) {
      return (
        <div className="flex-start-center-row-wrap height-14">
          <div className="normalText0">{providerLicenseKey}</div>
        </div>
      );
    }
  }, [signatures]);

  const getProviderDocId = useCallback(() => {
    const providerDocId = getVivaUniqueDocId(signatures);
    if (providerDocId) {
      return (
        <div className="flex-start-center-row-wrap height-14">
          <div className="normalText0">{providerDocId}</div>
        </div>
      );
    }
  }, [signatures]);

  const getAadeData = useCallback(() => {
    if (doc?.sentToAade) {
      return (
        <div className="flex-start-center-row-nowrap">
          <div className="flex-start-start-column-nowrap">
            <div className="flex-start-center-row-wrap height-14">
              <div className="normalText0 flex-start-center-row-nowrap">
                {doc?.aadeUID} (<div className="boldText0">{t('PDF.AADE_ID')}</div>)
              </div>
            </div>
            <div className="flex-start-center-row-wrap height-14">
              <div className="normalText0 flex-start-center-row-nowrap">
                {doc?.mark} (<div className="boldText0">{t('PDF.AADE_MARK')}</div>)
              </div>
            </div>
            {getProviderAuthCode()}
            {getProviderLicenseKey()}
            {getProviderDocId()}
          </div>
        </div>
      );
    }
  }, [doc?.aadeUID, doc?.mark, doc?.sentToAade, getProviderAuthCode, getProviderLicenseKey, getProviderDocId, t]);

  const getSection = useCallback(() => {
    return (
      <div className="flex-start-center-row-nowrap">
        <div className="min-width-240px">{getAadeData()}</div>
        <div className="flex-start-start-row-nowrap padding-l-120 padding-b-36">
          <div className="boldText1 padding-r-100">{t('PDF.PUBLISHER')}</div>
          <div className="boldText1">{t('PDF.RECEIVER')}</div>
        </div>
      </div>
    );
  }, [getAadeData, t]);

  return getSection();
};
